import { supabase } from "@/integrations/supabase/client";
import { carryOverDescription, readCarryOver } from "@/lib/onboarding/carryOver";
import { createAmpPlan } from "@/lib/amp/createPlan";
import { generateGuaranteedUniqueAssessmentId } from "@/utils/assessmentUtils";
import type { RiskPhase } from "@/lib/riskPhase";

export type StandaloneDocId =
  | "amp"
  | "risk-utforande"
  | "risk-bruksskede"
  | "arbetsomrade"
  | "arbetsberedning"
  | "skr"
  | "projektorsleverans"
  // Fristående dokument som lever i processvyn men kan köras helt ensamma.
  | "skyddsrond"
  | "tillbud"
  | "toolbox"
  | "dagslage"
  | "hetaarbeten"
  | "introduktion"
  | "samordningsmote"
  | "samrutin"
  | "forhandsanmalan"
  | "delegering"
  | "overlamning";

/** Rollen som äger dokumentet – styr filtret i Mina dokument. */
export type StandaloneRole = "basu" | "basp" | "projektor" | "byggherre" | "alla";

export const STANDALONE_ROLES: { key: StandaloneRole; label: string; description: string }[] = [
  { key: "alla", label: "Alla roller", description: "Hela katalogen med enskilda dokument." },
  {
    key: "basu",
    label: "Bas-U",
    description: "Systematik under produktionen: skyddsronder, tillbud, beredningar och introduktion.",
  },
  {
    key: "basp",
    label: "Bas-P",
    description: "Projekteringsskedet: arbetsområde, risker, arbetsmiljöplan och samordning.",
  },
  {
    key: "projektor",
    label: "Projektör",
    description: "Ditt eget projektörsansvar – risker i bruksskedet och leveransintyg.",
  },
  {
    key: "byggherre",
    label: "Byggherre",
    description: "Byggherrens uppgifter: förhandsanmälan, utseende av Bas-P/Bas-U och överlämning.",
  },
];

export interface StandaloneDocDef {
  id: StandaloneDocId;
  title: string;
  docNumber: string;
  /** Vad dokumentet är, i en mening. */
  what: string;
  /** När du behöver det. */
  when: string;
  icon: "file" | "shield" | "map" | "hardhat" | "gauge" | "package" | "clipboard" | "users" | "bell";
  /** Föreslaget namn på projektet/dokumentet. */
  defaultName: string;
  /** Vilken roll dokumentet hör till. */
  role: StandaloneRole;
  /** Lagrum som dokumentet svarar mot. */
  legalRef: string;
  /**
   * "full" = fungerar helt utan att någon annan part finns i systemet.
   * "partial" = går att köra ensamt men blir tomt utan underlag från andra.
   */
  standalone: "full" | "partial";
  /** Löpande dokument som förs som en serie (rond efter rond). */
  recurring?: boolean;
  /** Notis som visas när dokumentet behöver kompletteras manuellt. */
  note?: string;
}


export const STANDALONE_DOCS: StandaloneDocDef[] = [
  {
    id: "amp",
    title: "Arbetsmiljöplan (AMP)",
    docNumber: "Dok 12",
    what: "Projektets huvuddokument med rubriker enligt AFS 2023:3 och förifyllning från dina underlag.",
    when: "Krävs innan byggarbetsplatsen etableras när särskilda risker finns eller förhandsanmälan krävs.",
    icon: "file",
    defaultName: "Arbetsmiljöplan",
    role: "basp",
    legalRef: "AFS 2023:3 kap. 3",
    standalone: "partial",
    note: "Fungerar ensam, men rubriker som bygger på andras underlag får fyllas i manuellt.",
  },
  {
    id: "risk-utforande",
    title: "Risksammanställning – utförande",
    docNumber: "Dok 30b",
    what: "Risker under byggskedet: arbetsmoment, etablering och produktion.",
    when: "Under projektering och inför byggstart, som underlag till AMP och arbetsberedningar.",
    icon: "shield",
    defaultName: "Risksammanställning utförande",
    role: "basp",
    legalRef: "AFS 2023:3 – risker i byggskedet",
    standalone: "full",
  },
  {
    id: "risk-bruksskede",
    title: "Risksammanställning – bruksskede",
    docNumber: "Dok 31",
    what: "Kvarstående risker vid drift, underhåll och användning av byggnadsverket.",
    when: "Inför överlämning till förvaltning eller när projektörsansvaret ska dokumenteras.",
    icon: "shield",
    defaultName: "Risksammanställning bruksskede",
    role: "projektor",
    legalRef: "AFS 2023:3 – projektörens ansvar",
    standalone: "full",
  },
  {
    id: "arbetsomrade",
    title: "Arbetsområdesutredning",
    docNumber: "Dok 10",
    what: "Genomgång av arbetsområdets förutsättningar med ja/nej/ej aktuellt och motivering.",
    when: "Tidigt i projekteringen, innan riskerna sammanställs.",
    icon: "map",
    defaultName: "Arbetsområdesutredning",
    role: "basp",
    legalRef: "AFS 2023:3 – underlag till arbetsmiljöplan",
    standalone: "full",
  },
  {
    id: "arbetsberedning",
    title: "Arbetsberedning",
    docNumber: "Dok 23",
    what: "AI-stödd beredning av ett arbetsmoment med risker, skyddsåtgärder och kompetenskrav.",
    when: "Före riskfyllda moment – fungerar även fristående mot projekt som inte använder systemet.",
    icon: "hardhat",
    defaultName: "Arbetsberedning",
    role: "basu",
    legalRef: "AFS 2023:3 – särskilda risker",
    standalone: "full",
  },
  {
    id: "skr",
    title: "SKR-bedömning",
    docNumber: "Dok 05",
    what: "Bedömning av projektets storlek, komplexitet och risknivå.",
    when: "I starten, för att avgöra omfattningen på arbetsmiljöarbetet.",
    icon: "gauge",
    defaultName: "SKR-bedömning",
    role: "byggherre",
    legalRef: "AFS 2023:3 – byggherrens ansvar",
    standalone: "full",
  },
  {
    id: "projektorsleverans",
    title: "Projektörsleverans",
    docNumber: "Dok 18/19",
    what: "Rutin och leveransintyg som visar hur projektörsansvaret uppfylls, med verifieringskod.",
    when: "När du som projektör ska lämna in underlag till ett projekt utanför systemet.",
    icon: "package",
    defaultName: "Projektörsleverans",
    role: "projektor",
    legalRef: "AFS 2023:3 – projektörens ansvar",
    standalone: "full",
  },
  {
    id: "skyddsrond",
    title: "Skyddsrondsregister",
    docNumber: "Dok 40",
    what: "39-punktsprotokoll för skyddsronder – varje rond förs in i samma register med datum och nummer.",
    when: "Löpande under produktionen, oavsett om byggherren använder systemet.",
    icon: "clipboard",
    defaultName: "Skyddsronder",
    role: "basu",
    legalRef: "AFS 2023:1 (SAM) och AFS 2023:3",
    standalone: "full",
    recurring: true,
  },
  {
    id: "tillbud",
    title: "Tillbud och olyckor",
    docNumber: "Dok 28",
    what: "Rapportering och utredning av tillbud, olyckor och allvarliga händelser.",
    when: "Direkt när något inträffar – underlag för anmälan till Arbetsmiljöverket.",
    icon: "bell",
    defaultName: "Tillbud och olyckor",
    role: "basu",
    legalRef: "AML 3 kap. 3 a § och AFS 2023:1",
    standalone: "full",
    recurring: true,
  },
  {
    id: "toolbox",
    title: "Toolbox / skyddsmöten",
    docNumber: "Dok 44",
    what: "Kortmöten om risker på plats med ämne, deltagarlista och signering.",
    when: "Löpande i produktionen, som bevis på att informationen nått ut.",
    icon: "users",
    defaultName: "Toolbox-möten",
    role: "basu",
    legalRef: "AFS 2023:1 – information och medverkan",
    standalone: "full",
    recurring: true,
  },
  {
    id: "introduktion",
    title: "Skyddsintroduktion",
    docNumber: "Dok 22",
    what: "Introduktion av nya på arbetsplatsen med kvittens på genomgången.",
    when: "Innan någon börjar arbeta på arbetsplatsen.",
    icon: "users",
    defaultName: "Skyddsintroduktion",
    role: "basu",
    legalRef: "AFS 2023:3 – introduktion på byggarbetsplats",
    standalone: "full",
    recurring: true,
  },
  {
    id: "dagslage",
    title: "Dagslägesrapport",
    docNumber: "Dok 06c",
    what: "Dagens läge på arbetsplatsen: pågående arbeten, avvikelser och åtgärder.",
    when: "Dagligen eller veckovis som egen dokumentation av arbetsmiljöarbetet.",
    icon: "clipboard",
    defaultName: "Dagslägesrapporter",
    role: "basu",
    legalRef: "AFS 2023:1 – uppföljning",
    standalone: "full",
    recurring: true,
  },
  {
    id: "hetaarbeten",
    title: "Heta arbeten – tillstånd",
    docNumber: "Dok 45",
    what: "Tillstånd för heta arbeten med kontrollpunkter, bevakning och giltighetstid.",
    when: "Före svetsning, kapning eller annat brandfarligt arbete.",
    icon: "shield",
    defaultName: "Heta arbeten",
    role: "basu",
    legalRef: "AFS 2023:11 och försäkringsvillkor",
    standalone: "full",
    recurring: true,
  },
  {
    id: "samordningsmote",
    title: "Samordningsmöte",
    docNumber: "Dok 29",
    what: "Protokoll från samordningsmöte med deltagare, punkter och beslut.",
    when: "Löpande under projektering och produktion när flera parter samverkar.",
    icon: "users",
    defaultName: "Samordningsmöten",
    role: "basp",
    legalRef: "AFS 2023:3 – samordning",
    standalone: "partial",
    recurring: true,
    note: "Deltagare och punkter förs in manuellt när övriga parter inte finns i systemet.",
  },
  {
    id: "samrutin",
    title: "SAM-rutin",
    docNumber: "Dok 01",
    what: "Din egen rutin för systematiskt arbetsmiljöarbete – roller, ronder, uppföljning och årlig genomgång.",
    when: "Grunden i din systematik, oavsett vilket projekt du arbetar i.",
    icon: "file",
    defaultName: "SAM-rutin",
    role: "alla",
    legalRef: "AFS 2023:1 (systematiskt arbetsmiljöarbete)",
    standalone: "full",
  },
  {
    id: "forhandsanmalan",
    title: "Förhandsanmälan",
    docNumber: "Dok 08",
    what: "Förhandsanmälan till Arbetsmiljöverket med uppgifter om projektet och utsedda roller.",
    when: "När arbetet överskrider tidsgränserna i föreskrifterna.",
    icon: "bell",
    defaultName: "Förhandsanmälan",
    role: "byggherre",
    legalRef: "AFS 2023:3 – förhandsanmälan",
    standalone: "full",
  },
  {
    id: "delegering",
    title: "Utseende av Bas-P och Bas-U",
    docNumber: "Dok 04",
    what: "Skriftligt utseende av byggarbetsmiljösamordnare med uppgifter, befogenheter och kvittens.",
    when: "Innan projekteringen respektive byggarbetet startar.",
    icon: "file",
    defaultName: "Utseende av Bas-P och Bas-U",
    role: "byggherre",
    legalRef: "AFS 2023:3 – byggherrens utseende av Bas",
    standalone: "full",
  },
  {
    id: "overlamning",
    title: "Överlämning till förvaltning",
    docNumber: "Dok 32",
    what: "Överlämningsdokument med kvarstående risker och drift- och underhållsinformation.",
    when: "Vid avslut, när byggnadsverket lämnas över till förvaltningen.",
    icon: "package",
    defaultName: "Överlämning till förvaltning",
    role: "byggherre",
    legalRef: "AFS 2023:3 – bruksskedet",
    standalone: "partial",
    note: "Behöver underlag från projektörer – saknas de fyller du i riskerna manuellt.",
  },
];

/** Dokument som lever i processvyn – öppnas i fokusläge på en egen rutt. */
export const FOCUS_TABS: Partial<Record<StandaloneDocId, string>> = {
  skyddsrond: "skyddsrond",
  tillbud: "tillbud",
  toolbox: "toolbox",
  introduktion: "introduktion",
  dagslage: "dagslage",
  hetaarbeten: "hetaarbeten",
  samordningsmote: "samordning",
  samrutin: "samrutin",
  forhandsanmalan: "forhandsanmalan",
  delegering: "delegeringsbrev",
  overlamning: "overlamning",
};

/** Rutten där ett befintligt enskilt dokument öppnas. */
export const standaloneOpenRoute = (docId: StandaloneDocId, projectId: string): string =>
  FOCUS_TABS[docId] ? `/dokument/${docId}/${projectId}` : `/uppdrag/${projectId}`;

/** Grunduppsättning per roll – "starta min systematik". */
export const ROLE_PACKAGES: Partial<Record<StandaloneRole, StandaloneDocId[]>> = {
  basu: ["samrutin", "skyddsrond", "tillbud", "arbetsberedning"],
  basp: ["arbetsomrade", "risk-utforande", "amp"],
  projektor: ["risk-bruksskede", "projektorsleverans"],
  byggherre: ["skr", "delegering", "forhandsanmalan"],
};


export const getStandaloneDoc = (id: string): StandaloneDocDef | undefined =>
  STANDALONE_DOCS.find((d) => d.id === id);

/** Hämtar eller skapar standardmappen "Allmänt" i ett projekt. */
export async function ensureDefaultFolder(projectId: string, userId: string): Promise<string | null> {
  const { data: existing } = await supabase
    .from("folders")
    .select("id, name, parent_folder_id")
    .eq("project_id", projectId)
    .is("parent_folder_id", null);

  const found = (existing || []).find((f: any) => f?.name?.toLowerCase() === "allmänt");
  if (found?.id) return found.id as string;

  const { data, error } = await supabase
    .from("folders")
    .insert({
      name: "Allmänt",
      description: null,
      project_id: projectId,
      parent_folder_id: null,
      created_by: userId,
    })
    .select("id")
    .single();

  if (error || !data) return null;
  return data.id as string;
}

async function createRiskAssessment(
  projectId: string,
  folderId: string,
  name: string,
  phase: RiskPhase,
  userId: string
): Promise<string | null> {
  const assessmentId = await generateGuaranteedUniqueAssessmentId(projectId, folderId);

  const data = {
    status: "utkast",
    svar: {},
    projektNamn: name,
    assessmentName: name,
    projektbeskrivning: "",
    assessmentId,
    phase,
    metadata: {
      version: "1.0",
      created: new Date().toISOString(),
      projectId,
      folderId,
      phase,
      createdBy: userId,
    },
  };

  const { error } = await supabase.from("folder_risk_assessments").insert({
    folder_id: folderId,
    project_id: projectId,
    name,
    description: "",
    assessment_id: assessmentId,
    type: "riskbedomning",
    phase,
    data,
    created_by: userId,
    updated_by: userId,
  });

  if (error) return null;
  return assessmentId;
}

export interface StartStandaloneResult {
  projectId: string;
  route: string;
}

/**
 * Skapar ett enskilt dokument: projekt i standalone-läge, standardmapp
 * och själva dokumentet. Returnerar rutten där dokumentet öppnas.
 */
export async function startStandaloneDocument(
  docId: StandaloneDocId,
  name: string,
  userId: string
): Promise<StartStandaloneResult> {
  const doc = getStandaloneDoc(docId);
  const cleanName = (name || "").trim() || doc?.defaultName || "Nytt dokument";

  // Uppgifter från tidigare steg följer automatiskt med det nya dokumentet.
  const carried = carryOverDescription(readCarryOver());
  const baseDescription = `${doc?.title ?? "Dokument"} skapat ${new Date().toLocaleDateString("sv-SE")}`;

  const { data: project, error: projectError } = await supabase
    .from("projects")
    .insert({
      name: cleanName,
      user_id: userId,
      description: carried ? `${baseDescription} · ${carried}` : baseDescription,
      mode: "standalone",
      primary_document: docId,
    })
    .select("id")
    .single();

  if (projectError || !project) {
    throw new Error(projectError?.message || "Kunde inte skapa dokumentet");
  }

  const projectId = project.id as string;
  const folderId = await ensureDefaultFolder(projectId, userId);

  // Dokument som lever i processvyn öppnas i fokusläge på egen rutt.
  if (FOCUS_TABS[docId]) {
    return { projectId, route: `/dokument/${docId}/${projectId}` };
  }

  switch (docId) {

    case "amp": {
      const { planId } = await createAmpPlan({ projectId, folderId, name: cleanName, userId });
      if (!planId) throw new Error("Kunde inte skapa arbetsmiljöplanen");
      return { projectId, route: `/amp/${projectId}/${planId}` };
    }
    case "risk-utforande":
    case "risk-bruksskede": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      const phase: RiskPhase = docId === "risk-bruksskede" ? "bruksskede" : "byggskede";
      const assessmentId = await createRiskAssessment(projectId, folderId, cleanName, phase, userId);
      if (!assessmentId) throw new Error("Kunde inte skapa risksammanställningen");
      return {
        projectId,
        route: `/riskbedomning?projektId=${projectId}&folderId=${folderId}&assessmentId=${assessmentId}&steg=1`,
      };
    }
    case "arbetsomrade":
      return { projectId, route: `/arbetsomrade/${projectId}` };
    case "arbetsberedning": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      return { projectId, route: `/arbetsberedning/${projectId}/${folderId}` };
    }
    case "skr": {
      if (!folderId) throw new Error("Kunde inte skapa standardmappen");
      return { projectId, route: `/skr-bedomning/${projectId}/${folderId}` };
    }
    case "projektorsleverans":
      return { projectId, route: `/uppdrag/${projectId}?tools=basp` };
    default:
      return { projectId, route: `/uppdrag/${projectId}` };
  }
}

export interface RecentStandaloneDoc {
  projectId: string;
  docId: StandaloneDocId;
  title: string;
  name: string;
  updatedAt: string;
}

/** Hämtar användarens senaste enskilda dokument. */
export async function loadRecentStandaloneDocs(userId: string): Promise<RecentStandaloneDoc[]> {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, primary_document, updated_at, created_at")
    .eq("user_id", userId)
    .eq("mode", "standalone")
    .order("updated_at", { ascending: false })
    .limit(12);

  if (error || !data) return [];

  return data
    .filter((p: any) => !!p.primary_document)
    .map((p: any) => {
      const def = getStandaloneDoc(p.primary_document);
      return {
        projectId: p.id,
        docId: p.primary_document as StandaloneDocId,
        title: def?.title ?? "Dokument",
        name: p.name,
        updatedAt: p.updated_at || p.created_at,
      };
    });
}

/**
 * Raderar ett enskilt dokument (standalone-projekt) med tillhörande
 * bedömningar och mappar. Endast ägarens egna standalone-projekt kan raderas –
 * administratörer kan även radera andras dokument (`asAdmin`).
 */
export async function deleteStandaloneDocument(
  projectId: string,
  userId: string,
  asAdmin = false,
): Promise<void> {
  let lookup = supabase
    .from("projects")
    .select("id, user_id")
    .eq("id", projectId)
    .eq("mode", "standalone");

  if (!asAdmin) lookup = lookup.eq("user_id", userId);

  const { data: found, error: ownerError } = await lookup.maybeSingle();

  if (ownerError) throw new Error(ownerError.message);
  if (!found) {
    throw new Error(
      asAdmin
        ? "Dokumentet kunde inte hittas"
        : "Du kan bara ta bort dina egna dokument. Kontakta administratören för övriga.",
    );
  }

  await supabase.from("folder_risk_assessments").delete().eq("project_id", projectId);
  await supabase.from("folders").delete().eq("project_id", projectId);

  let del = supabase.from("projects").delete().eq("id", projectId);
  if (!asAdmin) del = del.eq("user_id", userId);

  const { error } = await del;
  if (error) throw new Error(error.message);

  // Spårbarhet: logga vem som raderade vad (och om det gjordes som admin).
  try {
    await supabase.rpc("log_security_event", {
      p_action_type: "delete_standalone_document",
      p_resource_type: "project",
      p_resource_id: projectId,
      p_details: {
        as_admin: asAdmin && (found as any).user_id !== userId,
        owner_id: (found as any).user_id,
      } as any,
    });
  } catch {
    /* loggning ska aldrig blockera raderingen */
  }
}

/**
 * Flyttar ett enskilt dokument in i ett riktigt projekt.
 * Allt innehåll får det nya projektets id och den valda mappen, därefter
 * raderas det tomma standalone-projektet.
 */
export async function moveStandaloneToProject(
  standaloneProjectId: string,
  targetProjectId: string,
  targetFolderId: string | null,
  userId: string,
): Promise<void> {
  const { data: source, error: sourceError } = await supabase
    .from("projects")
    .select("id, user_id")
    .eq("id", standaloneProjectId)
    .eq("mode", "standalone")
    .eq("user_id", userId)
    .maybeSingle();

  if (sourceError) throw new Error(sourceError.message);
  if (!source) throw new Error("Du kan bara flytta dina egna dokument.");

  const { data: target, error: targetError } = await supabase
    .from("projects")
    .select("id")
    .eq("id", targetProjectId)
    .maybeSingle();

  if (targetError) throw new Error(targetError.message);
  if (!target) throw new Error("Projektet kunde inte hittas.");

  const withFolder = { project_id: targetProjectId, folder_id: targetFolderId } as any;

  const moves = [
    supabase.from("folder_risk_assessments").update(withFolder).eq("project_id", standaloneProjectId),
    supabase.from("skr_assessments").update(withFolder).eq("project_id", standaloneProjectId),
    supabase.from("work_preparations").update(withFolder).eq("project_id", standaloneProjectId),
    supabase.from("work_environment_plans").update(withFolder).eq("project_id", standaloneProjectId),
    supabase.from("amp_sections").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("amp_versions").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    // Arbetsområdesutredning (Dok 10) – annars raderas innehållet med projektet.
    supabase.from("work_area_surveys").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    // Projektörsleverans (Dok 18/19)
    supabase
      .from("designer_submissions")
      .update({ project_id: targetProjectId })
      .eq("project_id", standaloneProjectId),
    supabase.from("designer_routines").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase
      .from("designer_certificates")
      .update({ project_id: targetProjectId })
      .eq("project_id", standaloneProjectId),
    // Fristående Bas-U- och byggherredokument följer också med.
    supabase.from("safety_rounds").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("incident_reports").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("toolbox_talks").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("safety_inductions").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("hot_work_permits").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase
      .from("coordination_meetings")
      .update({ project_id: targetProjectId })
      .eq("project_id", standaloneProjectId),
    supabase.from("sam_routines").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase
      .from("prior_notifications")
      .update({ project_id: targetProjectId })
      .eq("project_id", standaloneProjectId),
    supabase.from("delegation_letters").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase.from("delegation_orders").update({ project_id: targetProjectId }).eq("project_id", standaloneProjectId),
    supabase
      .from("handover_documents")
      .update({ project_id: targetProjectId })
      .eq("project_id", standaloneProjectId),
  ];



  const results = await Promise.all(moves);
  const failed = results.find((r: any) => r?.error);
  if (failed?.error) throw new Error(failed.error.message);

  // Det tomma dokumentprojektet och dess standardmapp behövs inte längre.
  await supabase.from("folders").delete().eq("project_id", standaloneProjectId);
  const { error: deleteError } = await supabase
    .from("projects")
    .delete()
    .eq("id", standaloneProjectId)
    .eq("user_id", userId);

  if (deleteError) throw new Error(deleteError.message);
}
